import { inject, Injectable } from '@angular/core';
import { FileService, PageService, PrefabCacheService, ProjectService, WmHttpService } from "@wms/services";
import { environment } from "@wms/app";
import { getPrefabConfigUrl } from "@wms/design-container";
import { isArray } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class PrefabService {
  private TARGET: string = "Prefab";
  private API_META: any = {
    upload: {
      url: environment.SERVICES_BASE_PATH + "services/import",
      method: "POST",
      params: {
        "packageType": 'PREFAB'
      }
    },
    register: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs",
      method: "POST"
    },
    deRegister: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs",
      method: "DELETE"
    },
    getConfig: {
      url: "prefabs/:prefab/webapp/config.json",
      method: "GET"
    },
    list: {
      url: environment.SERVICES_BASE_PATH + "services/prefabs?projectID=:projectID",
      method: "GET",
      embedModeAPI: true
    },
    remove: {
      url: environment.SERVICES_BASE_PATH + "services/prefabs/:prefabID/versions/:versionID?projectId=:projectId",
      method: "DELETE"
    },
    getServerProps: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/properties/metadata",
      method: "GET"
    },
    setServerProps: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/properties/metadata",
      method: "POST"
    },
    updateAppProps: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/properties",
      method: "PUT"
    },
    export: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/export",
      method: "POST"
    },
    publishPrefabToProject: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/publish/app?forceRegister=:forceRegister",
      method: "POST"
    },
    getAppPrefabServerProps: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/:prefabName/properties",
      method: "GET"
    },
    setAppPrefabServerProps: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/:prefabName/properties",
      method: "POST"
    },
    getProfile: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/profiles/:profileName',
      method: 'GET'
    },
  }
  activePrefabConfig: any;
  private projectService: ProjectService;
  private pageService: PageService;
  private fileService: FileService;
  private appPrefabNameConfigMap: { [key: string]: any } = {};
  private prefabCacheService: PrefabCacheService;

  constructor(private wmHttpService: WmHttpService) {
    this.wmHttpService.registerApiMeta(this.TARGET, this.API_META);
    this.projectService = inject(ProjectService);
    this.pageService = inject(PageService);
    this.fileService = inject(FileService);
    this.prefabCacheService = inject(PrefabCacheService);
  }

  upload(uploadConfig: any): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: uploadConfig.action,
      data: uploadConfig.content
    }) as Promise<any>);
  }

  getRequestParams(params: any) {
    let options = ['forceRegister='+params.forceRegister, 'upgrade='+!!params.upgrade];
    return options.join('&');
  }

  register(params: any) {
    let url = environment.SERVICES_BASE_PATH + 'services/projects/' + params.projectId + '/prefabs?' + this.getRequestParams(params),
      dataObj = JSON.stringify({
      "artifactId": params.prefabId,
      "versionId": params.versionId
    });
    const headers = {'Content-Type': 'application/json'};
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'register',
      url: url,
      data: dataObj,
      headers: headers
    }) as Promise<any>);
  }

  deRegister(params: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "deRegister",
      urlParams: {
        projectID: params.projectId
      },
      data: params.data.prefabName
    }) as Promise<any>);
  }

  getAppPrefabServerProps(params: any): Promise<any> {
    // fetch the properties from prefabCache, or it is not available fallback to making call
    return this.prefabCacheService.getPrefabDataByType({
      type: 'properties',
      prefabName: params.prefabName
    });
  }

  getConfig(prefabName?: string, recallConfig?: boolean) {
    return new Promise((res, rej) => {
      if (this.activePrefabConfig && !recallConfig) {
        // this.activePrefabConfig.properties is always an object
        if (this.activePrefabConfig.properties && !isArray(this.activePrefabConfig.properties)) {
          return res(this.activePrefabConfig);
        }
      }
      let url = getPrefabConfigUrl(this.projectService.getProjectId(), prefabName);
      return url && this.pageService.fetchContent(url).then((response: any): void => {
        this.activePrefabConfig = response;
        res(response);
      });
    });
  }

  setConfig(config: any): Promise<any> {
    this.activePrefabConfig = config;
    return this.fileService.write("config.json", config);
  }

  removePrefab(params: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "remove",
      urlParams: {
        prefabID: params.prefabID,
        versionID: params.versionId,
        projectId: this.projectService.getProjectId()
      }
    }) as Promise<any>);
  }

  getServerProps() {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "getServerProps",
      urlParams: {
        projectID: this.projectService.getProjectId()
      }
    }) as Promise<any>);
  }

  export(params: any): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'export',
      data: {
        "exportType": params.exportType,
        "targetName": params.targetName
      },
      urlParams: {
        projectID: params.projectId
      }
    }) as Promise<any>);
  }

  list() {
    return this.wmHttpService.execute({
      target: 'Prefab',
      action: 'list',
      urlParams: {
        projectID: this.projectService.getProjectId()
      }
    });
  }

  setServerProps(data: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "setServerProps",
      urlParams: {
        projectID: this.projectService.getProjectId()
      },
      data: data
    }) as Promise<any>);
  }

  updateAppProps(data: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "updateAppProps",
      urlParams: {
        projectID: this.projectService.getProjectId()
      },
      data: data
    }) as Promise<any>);
  }

  getProfile(profileName: string) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "getProfile",
      urlParams: {
        projectID: this.projectService.getProjectId(),
        profileName: profileName
      }
    }) as Promise<any>);
  }
  updatePrefabs(forceRegister:string, data:any, projectId:string) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'publishPrefabToProject',
      pathParams:{
        projectID: projectId,
        forceRegister: forceRegister
      },
      data:data
    }) as Promise<any>);
  }

  setAppPrefabServerProps(data: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "setAppPrefabServerProps",
      urlParams: {
        projectID: this.projectService.getProjectId(),
        prefabName: data.prefabName
      },
      data: data.data
    }) as Promise<any>);
  }
  setPrefabConfig(prefabName: string, data: any) {
    this.appPrefabNameConfigMap[prefabName] = data;
  }
  getPrefabConfig(){
    return this.appPrefabNameConfigMap
  }
}
